import { useMemo, useState } from 'react'
import {
  Box,
  Card,
  CardContent,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Tooltip,
  Typography,
} from '@mui/material'
import { fmtBrl } from '../../api/bridge'

export type LinhaDia = {
  data: string
  dia: string
  valor: number
  pedidos: number
  /** Itens vendidos (Σ quantidade). `null` = o backend ainda não devolve. */
  itens?: number | null
  /** MC oficial (notas fiscais) do dia, em R$. `null` = não informado. */
  mc?: number | null
  /** MC ajustada (com créditos) do dia, em R$ — regra da skill margem-ajustada-fiesta. */
  mcAj?: number | null
  /** Receita dos pedidos com margem: denominador da MC %. */
  recMc?: number | null
}

type Coluna = 'data' | 'pedidos' | 'itens' | 'valor' | 'ticket' | 'mcAj' | 'mcAjPct' | 'mc' | 'mcPct'

/**
 * Ordem das colunas pedida pela Mariana em 22/09/2026, no modelo do Upseller:
 * Pedidos → Itens vendidos → Valor total → e a MC ajustada em R$ e %. A oficial fica ao lado,
 * em cinza — as duas margens andam sempre juntas (skill margem-ajustada-fiesta).
 */
const COLUNAS: Array<{ id: Coluna; label: string; numerica: boolean; tom?: 'aj' | 'muted' }> = [
  { id: 'data', label: 'Data', numerica: false },
  { id: 'pedidos', label: 'Pedidos', numerica: true },
  { id: 'itens', label: 'Itens vendidos', numerica: true },
  { id: 'valor', label: 'Valor total', numerica: true },
  { id: 'ticket', label: 'Ticket médio', numerica: true, tom: 'muted' },
  { id: 'mcAj', label: 'MC ajustada R$', numerica: true, tom: 'aj' },
  { id: 'mcAjPct', label: 'MC ajustada %', numerica: true, tom: 'aj' },
  { id: 'mc', label: 'MC oficial R$', numerica: true, tom: 'muted' },
  { id: 'mcPct', label: 'MC oficial %', numerica: true, tom: 'muted' },
]

const ticketDe = (l: LinhaDia) => (l.pedidos > 0 ? l.valor / l.pedidos : 0)
const pctDe = (mc: number | null | undefined, rec: number | null | undefined) =>
  mc == null || !rec ? null : (100 * mc) / rec

function valorDa(l: LinhaDia, col: Coluna): number | string | null {
  switch (col) {
    case 'data': return l.data
    case 'pedidos': return l.pedidos
    case 'itens': return l.itens ?? null
    case 'valor': return l.valor
    case 'ticket': return ticketDe(l)
    case 'mcAj': return l.mcAj ?? null
    case 'mcAjPct': return pctDe(l.mcAj, l.recMc)
    case 'mc': return l.mc ?? null
    case 'mcPct': return pctDe(l.mc, l.recMc)
  }
}

const fmtPct = (v: number | null) => (v == null ? '—' : `${v.toFixed(1).replace('.', ',')}%`)
const fmtNum = (v: number | null) => (v == null ? '—' : v.toLocaleString('pt-BR'))
const fmtRs = (v: number | null) => (v == null ? '—' : fmtBrl(v))
/** Cor da margem pela régua da casa: verde a partir de 20%, amarelo entre 0 e 20, vermelho negativo. */
const corMc = (pct: number | null) => (pct == null ? undefined : pct < 0 ? 'error.main' : pct < 20 ? 'warning.main' : 'success.main')

/**
 * Detalhe dia a dia do período, com a linha "Resumo" fixa no topo — o formato
 * da tabela do Upseller.
 *
 * O resumo é somado a partir das próprias linhas, então ele sempre fecha com o
 * que está à vista. Ordenar não mexe nele. Coluna sem dado do backend escreve "—",
 * nunca zero.
 */
export function TabelaPorDia({ linhas }: { linhas: LinhaDia[] }) {
  const [ordem, setOrdem] = useState<Coluna>('data')
  const [desc, setDesc] = useState(true)

  const temItens = linhas.some((l) => l.itens != null)
  const temMc = linhas.some((l) => l.mc != null || l.mcAj != null)

  const resumo = useMemo(() => {
    const soma = (f: (l: LinhaDia) => number | null | undefined) => {
      let tem = false
      let s = 0
      for (const l of linhas) {
        const v = f(l)
        if (v != null) { tem = true; s += v }
      }
      return tem ? s : null
    }
    const pedidos = linhas.reduce((s, l) => s + l.pedidos, 0)
    const valor = linhas.reduce((s, l) => s + l.valor, 0)
    const r: LinhaDia = {
      data: 'resumo', dia: 'Resumo', pedidos, valor,
      itens: soma((l) => l.itens), mc: soma((l) => l.mc), mcAj: soma((l) => l.mcAj), recMc: soma((l) => l.recMc),
    }
    return r
  }, [linhas])

  const ordenadas = useMemo(() => {
    const copia = [...linhas]
    copia.sort((a, b) => {
      const va = valorDa(a, ordem)
      const vb = valorDa(b, ordem)
      if (typeof va === 'string' || typeof vb === 'string') {
        const cmp = String(va ?? '').localeCompare(String(vb ?? ''))
        return desc ? -cmp : cmp
      }
      const cmp = (va ?? Number.NEGATIVE_INFINITY) - (vb ?? Number.NEGATIVE_INFINITY)
      return desc ? -cmp : cmp
    })
    return copia
  }, [linhas, ordem, desc])

  const clicar = (col: Coluna) => {
    if (col === ordem) setDesc((d) => !d)
    else { setOrdem(col); setDesc(true) }
  }

  const celulas = (l: LinhaDia) => {
    const ajPct = pctDe(l.mcAj, l.recMc)
    const ofPct = pctDe(l.mc, l.recMc)
    return (
      <>
        <TableCell sx={{ whiteSpace: 'nowrap' }}>{l.dia}</TableCell>
        <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{fmtNum(l.pedidos)}</TableCell>
        <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{fmtNum(l.itens ?? null)}</TableCell>
        <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{fmtBrl(l.valor)}</TableCell>
        <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', color: 'text.secondary' }}>{l.pedidos > 0 ? fmtBrl(ticketDe(l)) : '—'}</TableCell>
        <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: 700, color: corMc(ajPct) }}>{fmtRs(l.mcAj ?? null)}</TableCell>
        <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: 700, color: corMc(ajPct) }}>{fmtPct(ajPct)}</TableCell>
        <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', color: 'text.secondary' }}>{fmtRs(l.mc ?? null)}</TableCell>
        <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', color: 'text.secondary' }}>{fmtPct(ofPct)}</TableCell>
      </>
    )
  }

  if (!linhas.length) {
    return (
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>Dia a dia</Typography>
          <Typography variant="body2" color="text.secondary">Sem série diária no período selecionado.</Typography>
        </CardContent>
      </Card>
    )
  }

  return (
    <Card>
      <CardContent sx={{ pb: 1 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="baseline" spacing={1} flexWrap="wrap">
          <Typography variant="h6">Dia a dia</Typography>
          <Typography variant="caption" color="text.secondary">{linhas.length} dia(s) · clique no cabeçalho para ordenar</Typography>
        </Stack>
      </CardContent>
      <TableContainer sx={{ maxHeight: 460 }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              {COLUNAS.map((c) => (
                <TableCell
                  key={c.id}
                  align={c.numerica ? 'right' : 'left'}
                  sortDirection={ordem === c.id ? (desc ? 'desc' : 'asc') : false}
                  sx={{ whiteSpace: 'nowrap', fontWeight: 700, bgcolor: c.tom === 'aj' ? 'rgba(46,125,50,.08)' : undefined, color: c.tom === 'muted' ? 'text.secondary' : undefined }}
                >
                  <Tooltip title={c.tom === 'aj' ? 'MC ajustada (com créditos): oficial + PIS/COFINS efetivo (skill). Incentivo Extrema ainda NÃO entra nesta coluna — margens não identifica a conta ML.' : c.tom === 'muted' && c.id !== 'ticket' ? 'MC oficial (notas fiscais): calculated_margin de margens' : ''}>
                    <TableSortLabel active={ordem === c.id} direction={ordem === c.id && !desc ? 'asc' : 'desc'} onClick={() => clicar(c.id)}>
                      {c.label}
                    </TableSortLabel>
                  </Tooltip>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow sx={{ '& td': { fontWeight: 700, bgcolor: 'action.hover', position: 'sticky', top: 37, zIndex: 1 } }}>
              {celulas(resumo)}
            </TableRow>
            {ordenadas.map((l) => (
              <TableRow key={l.data || l.dia} hover>{celulas(l)}</TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <Box sx={{ px: 2, py: 1 }}>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
          Vendas válidas = pedidos pagos, sem cancelados. MC % sobre a receita dos pedidos com margem; pedido sem CMV fica fora das duas margens.
          {!temItens || !temMc
            ? ' As colunas com "—" ainda não vêm da API (itens vendidos e margens por dia) — ver entrega_dev_vendas_diarias/LEIA-ME_DEV.md.'
            : ''}
        </Typography>
      </Box>
    </Card>
  )
}
